// ============================================================================
// NavigationContext — cross-screen "open this record" seam.
//
// A report (or any screen) can ask the app to open a specific record in another
// screen's tab, e.g. drilling from a sales report to the actual Sales Invoice.
//
//   const { openRecord } = useNavigation();
//   openRecord('sales-invoice', { recordId: inv.id, mode: 'edit' });
//
// The target screen reacts to its pending target and clears it:
//
//   useNavTarget('sales-invoice', (t) => handleOpenById(t.recordId, t.mode));
//
// SCOPE RULE (enforced by the CALLER, not here): cross-screen navigation is
// limited to the logged Entity + Business Unit — the transaction screens operate
// in the active X-Entity-ID / X-Business-Unit-ID context, so a document from a
// different entity/BU must NOT be opened here. Callers check the target's scope
// against the active scope before calling openRecord (see canOpenInScope()).
// ============================================================================

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

export interface NavTarget {
  recordId: string;
  /** How to open it in the target screen. Defaults to 'edit'. */
  mode?: 'edit' | 'view';
  /** Optional human label for logging/telemetry. */
  label?: string;
  /**
   * Optional: what the caller wants done with the record when it is not the target screen's
   * own document — e.g. 'from-debit-note' asks Supplier Set-off to start a new set-off with
   * that debit note's on-account balance pre-picked (recordId = the debit note).
   */
  intent?: string;
}

interface NavigationContextType {
  /** Pending target per screen code. */
  targets: Record<string, NavTarget | undefined>;
  /** Open a record in another screen's tab (also activates/opens that tab). */
  openRecord: (screenCode: string, target: NavTarget) => void;
  /** Just open/activate a screen's tab by its catalog screen code, with no record target (the Setup links). */
  openScreen: (screenCode: string) => void;
  /**
   * Switch the active entity (the layout's scope switch: tabs collapse, permissions reload)
   * and then open a screen — the Setup page's drill-down into another entity's item. The
   * caller confirms with the user first; this does not prompt.
   */
  switchEntityAndOpen: (entityId: string, entityName: string, screenCode: string) => Promise<void>;
  /** A target screen reads + clears its pending target. */
  consumeNavTarget: (screenCode: string) => void;
}

const NavigationContext = createContext<NavigationContextType | null>(null);

export const NavigationProvider: React.FC<{
  /** Opens/activates the tab for a screen code (the layout's handleModuleSelect). */
  onNavigateToScreen: (screenCode: string) => void;
  /** Switches the active entity through the layout's scope switch, then opens the screen. */
  onSwitchEntityAndOpen?: (entityId: string, entityName: string, screenCode: string) => Promise<void>;
  children: React.ReactNode;
}> = ({ onNavigateToScreen, onSwitchEntityAndOpen, children }) => {
  const [targets, setTargets] = useState<Record<string, NavTarget | undefined>>({});

  const openRecord = useCallback((screenCode: string, target: NavTarget) => {
    setTargets(prev => ({ ...prev, [screenCode]: target }));
    onNavigateToScreen(screenCode);
  }, [onNavigateToScreen]);

  const openScreen = useCallback((screenCode: string) => {
    onNavigateToScreen(screenCode);
  }, [onNavigateToScreen]);

  const switchEntityAndOpen = useCallback(async (entityId: string, entityName: string, screenCode: string) => {
    if (onSwitchEntityAndOpen) await onSwitchEntityAndOpen(entityId, entityName, screenCode);
    else onNavigateToScreen(screenCode);
  }, [onSwitchEntityAndOpen, onNavigateToScreen]);

  const consumeNavTarget = useCallback((screenCode: string) => {
    setTargets(prev => {
      if (!prev[screenCode]) return prev;
      const next = { ...prev };
      delete next[screenCode];
      return next;
    });
  }, []);

  return (
    <NavigationContext.Provider value={{ targets, openRecord, openScreen, switchEntityAndOpen, consumeNavTarget }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextType => {
  const ctx = useContext(NavigationContext);
  if (!ctx) {
    // Safe no-op fallback so a screen used outside the provider (tests, storybook)
    // doesn't crash — navigation simply does nothing.
    return {
      targets: {},
      openRecord: () => { /* no provider */ },
      openScreen: () => { /* no provider */ },
      switchEntityAndOpen: async () => { /* no provider */ },
      consumeNavTarget: () => { /* no provider */ },
    };
  }
  return ctx;
};

/**
 * Target-screen helper: when a navigation target for `screenCode` appears, run
 * `handler` once and clear it. Handler is kept in a ref so it need not be memoised.
 */
export const useNavTarget = (screenCode: string, handler: (t: NavTarget) => void): void => {
  const { targets, consumeNavTarget } = useNavigation();
  const target = targets[screenCode];
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (target) {
      handlerRef.current(target);
      consumeNavTarget(screenCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
};

/**
 * Scope gate used by CALLERS before opening a document in another screen.
 * Returns true only when the document's entity (and BU, when the document has one)
 * matches the currently active entity/BU — because the transaction screens work in
 * that header context. A document with no BU is openable within its entity.
 */
export const canOpenInScope = (
  docEntityId: string | null | undefined,
  docBusinessUnitId: string | null | undefined,
  activeEntityId: string | null | undefined,
  activeBusinessUnitId: string | null | undefined,
): boolean => {
  if (!docEntityId || !activeEntityId) return false;
  if (docEntityId !== activeEntityId) return false;
  if (docBusinessUnitId && activeBusinessUnitId && docBusinessUnitId !== activeBusinessUnitId) return false;
  return true;
};
